import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type Option, MultiSelect, SingleSelect } from "@sprout/select";
import { TextInput } from "@sprout/text_input";
import { type DemographicAnswer } from "../../utils/jben/build_application";

export interface DemographicFieldOption extends Option {
  freeform?: boolean;
}
export type DemographicFieldOptions = DemographicFieldOption[];

export interface DemographicFieldProps {
  label: string;
  name: string;
  required: boolean;
  type: "multi_value_single_select" | "multi_value_multi_select";
  options: DemographicFieldOptions;
  error?: string | null;
  onChange: (value: DemographicAnswer[]) => void;
}

export const DemographicField = ({
  label,
  name,
  required,
  type,
  options,
  error,
  onChange,
}: DemographicFieldProps) => {
  const { t } = useTranslation("job_post");

  const [singleSelected, setSingleSelected] = useState<DemographicFieldOption | null>(null);
  const [multiSelected, setMultiSelected] = useState<DemographicFieldOption[]>([]);
  const [showFreeform, setShowFreeform] = useState(false);
  const [freeformValue, setFreeformValue] = useState("");

  const freeformId = options.find((option) => option.freeform)?.value;

  const propagate = (selectedOptions: DemographicFieldOptions) => {
    const newValue: DemographicAnswer[] = selectedOptions.map((selectedOption) => {
      if (selectedOption.freeform) {
        return { id: selectedOption.value.toString(), freeform: freeformValue };
      }
      return { id: selectedOption.value.toString() };
    });

    const shouldShowFreeform = selectedOptions.some((selectedOption) => selectedOption.freeform);
    setShowFreeform(shouldShowFreeform);
    if (!shouldShowFreeform) setFreeformValue("");

    onChange(newValue);
  };

  const handleSingleChange = (selected: Option | null) => {
    const option = selected as DemographicFieldOption | null;
    setSingleSelected(option);
    propagate(option ? [option] : []);
  };

  const handleMultiChange = (selected: Option[]) => {
    const opts = selected as DemographicFieldOption[];
    setMultiSelected(opts);
    propagate(opts);
  };

  const handleFreeformChange = (value: string) => {
    setFreeformValue(value);
    const source =
      type === "multi_value_multi_select" ? multiSelected : singleSelected ? [singleSelected] : [];
    const newValue: DemographicAnswer[] = source.map((selectedOption) => {
      if (selectedOption.freeform) {
        return { id: freeformId!.toString(), freeform: value };
      }
      return { id: selectedOption.value.toString() };
    });
    onChange(newValue);
  };

  return (
    <>
      {type === "multi_value_multi_select" ? (
        <MultiSelect
          label={label}
          options={options}
          onChange={handleMultiChange}
          values={multiSelected}
          error={error}
          required={required}
          clearable
        />
      ) : (
        <SingleSelect
          label={label}
          options={options}
          onChange={handleSingleChange}
          value={singleSelected}
          error={error}
          required={required}
          clearable
        />
      )}
      {showFreeform && (
        <TextInput
          id={`${name}-freeform`}
          label={t("demographics.self_describe_label")}
          maxLength={255}
          value={freeformValue}
          onChange={(event) => handleFreeformChange(event.currentTarget.value)}
          autoFocus
        />
      )}
    </>
  );
};
